import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Spinner from '../components/Spinner';
import WizardShell from '../components/onboarding/WizardShell';
import {FALLBACK_STEPS, STEP_CODES, STEP_TITLES} from '../components/onboarding/constants';
import StepWelcome from '../components/onboarding/steps/StepWelcome';
import StepStrategy from '../components/onboarding/steps/StepStrategy';
import StepProfile from '../components/onboarding/steps/StepProfile';
import StepSizes from '../components/onboarding/steps/StepSizes';
import StepStyle from '../components/onboarding/steps/StepStyle';
import StepActivities from '../components/onboarding/steps/StepActivities';
import StepConsents from '../components/onboarding/steps/StepConsents';
import StepLocation from '../components/onboarding/steps/StepLocation';
import StepAiMemory from '../components/onboarding/steps/StepAiMemory';
import StepFirstItem from '../components/onboarding/steps/StepFirstItem';
import StepChannels from '../components/onboarding/steps/StepChannels';
import StepDone from '../components/onboarding/steps/StepDone';
import {StepProps} from '../components/onboarding/steps/types';
import {
    OnboardingStepMeta,
    completeOnboarding,
    fetchOnboardingState,
    fetchOnboardingSteps,
    isOnboardingClosed,
    saveOnboardingStep,
    skipOnboarding,
} from '../support/onboardingApi';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

/** Экран шага по коду из контракта шагов; неизвестный код мастер просто пропускает. */
const STEP_COMPONENTS: Record<string, React.FC<StepProps>> = {
    [STEP_CODES.welcome]: StepWelcome,
    [STEP_CODES.strategy]: StepStrategy,
    [STEP_CODES.profile]: StepProfile,
    [STEP_CODES.sizes]: StepSizes,
    [STEP_CODES.style]: StepStyle,
    [STEP_CODES.activities]: StepActivities,
    [STEP_CODES.consents]: StepConsents,
    [STEP_CODES.location]: StepLocation,
    [STEP_CODES.aiMemory]: StepAiMemory,
    [STEP_CODES.firstItem]: StepFirstItem,
    [STEP_CODES.channels]: StepChannels,
    [STEP_CODES.done]: StepDone,
};

/**
 * Мастер онбординга (CTHS-216/217, эпик CTHS-157).
 *
 * Набор шагов приходит с backend (`GET /api/onboarding/steps`) — страница не держит своего
 * списка и не знает, какие слоты выбыли из мастера. Ответ каждого шага сохраняется
 * `PATCH /api/onboarding/step/{n}` по кнопке «Далее»; переход — только после успеха.
 * Возобновление — с `nextStep` состояния, повторный показ после `COMPLETED`/`SKIPPED`
 * невозможен (страница сразу уводит на главную).
 */
const OnboardingPage: React.FC = () => {
    const navigate = useNavigate();
    useDocumentTitle();

    const [steps, setSteps] = useState<OnboardingStepMeta[]>(FALLBACK_STEPS);
    const [answers, setAnswers] = useState<Record<string, unknown>>({});
    const [index, setIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    // Мастер закрыт (COMPLETED/SKIPPED) или уже завершён: страница уходит на главную и
    // ничего не рисует — иначе на кадр показался бы первый шаг пройденного мастера.
    const [leaving, setLeaving] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadFailed(false);
        try {
            const [state, contract] = await Promise.all([
                fetchOnboardingState(true),
                fetchOnboardingSteps().catch(() => FALLBACK_STEPS),
            ]);
            if (isOnboardingClosed(state)) {
                setLeaving(true);
                navigate('/home', {replace: true});
                return;
            }
            const activeSteps = contract.length > 0 ? contract : FALLBACK_STEPS;
            setSteps(activeSteps);
            setAnswers(state.stepAnswers || {});
            const resumeAt = activeSteps.findIndex(s => s.number === state.nextStep);
            setIndex(resumeAt >= 0 ? resumeAt : 0);
        } catch {
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        load();
    }, [load]);

    const step = steps[index];
    const isLast = index === steps.length - 1;

    const setAnswer = useCallback((answer: unknown) => {
        setAnswers(prev => ({...prev, [String(step?.number)]: answer}));
    }, [step]);

    const goTo = (nextIndex: number) => {
        setError(null);
        setIndex(Math.min(Math.max(nextIndex, 0), steps.length - 1));
    };

    const handleNext = async () => {
        if (!step) {
            return;
        }
        if (isLast) {
            await handleComplete();
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await saveOnboardingStep(step.number, answers[String(step.number)] ?? null);
            goTo(index + 1);
        } catch {
            setError('Не удалось сохранить шаг. Проверьте соединение и попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };

    const handleComplete = async () => {
        setSaving(true);
        setError(null);
        try {
            await completeOnboarding();
            setLeaving(true);
            navigate('/home', {replace: true});
        } catch {
            setError('Не удалось завершить настройку. Попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };

    const handleSkipAll = async () => {
        setSaving(true);
        setError(null);
        try {
            await skipOnboarding();
            setLeaving(true);
            navigate('/home', {replace: true});
        } catch {
            setError('Не удалось пропустить настройку. Попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    };

    const nextLabel = useMemo(() => {
        if (isLast) {
            return 'Готово';
        }
        return index === 0 ? 'Начать' : 'Далее';
    }, [index, isLast]);

    if (leaving) {
        return null;
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ink-50 dark:bg-ink-950">
                <Spinner/>
            </div>
        );
    }

    if (loadFailed || !step) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 bg-ink-50 dark:bg-ink-950">
                <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="onboarding-error">
                    Не удалось загрузить настройку. Попробуйте ещё раз.
                </p>
                <div className="flex gap-2">
                    <button type="button" onClick={load} className="btn-primary min-h-[44px]"
                            data-testid="onboarding-retry">
                        Повторить
                    </button>
                    <button type="button" onClick={() => navigate('/home')} className="btn-secondary min-h-[44px]"
                            data-testid="onboarding-to-home">
                        На главную
                    </button>
                </div>
            </div>
        );
    }

    const StepComponent = STEP_COMPONENTS[step.code];
    const meta = STEP_TITLES[step.code] ?? {title: 'Настройка', subtitle: ''};

    return (
        <WizardShell
            order={step.order}
            totalSteps={steps.length}
            title={meta.title}
            subtitle={meta.subtitle}
            nextLabel={nextLabel}
            skippable={step.skippable}
            canGoBack={index > 0}
            canSkipAll={!isLast}
            saving={saving}
            error={error}
            onNext={handleNext}
            onBack={() => goTo(index - 1)}
            onSkipStep={() => goTo(index + 1)}
            onSkipAll={handleSkipAll}
        >
            {StepComponent
                ? <StepComponent value={answers[String(step.number)]} onChange={setAnswer} answers={answers}/>
                : <p className="text-sm text-gray-500 dark:text-gray-400">Шаг недоступен в этой версии приложения.</p>}
        </WizardShell>
    );
};

export default OnboardingPage;
